import { NetworkTestOptions } from "opentok-network-test-js";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "store";
import logger from "logging/logger";
import { supportsSetSinkId } from "../utils";
import { DeviceInfoState, PermissionState } from "../types";

const selectDeviceInfoState = (state: RootState): DeviceInfoState =>
  state.deviceInfo;

export const getDevices = ({
  devices,
}: {
  devices: MediaDeviceInfo[];
}): MediaDeviceInfo[] => devices;

export const selectDevices = createSelector(selectDeviceInfoState, getDevices);

export const isVideoInputDevice = (device: MediaDeviceInfo): boolean =>
  device.kind === "videoinput";

export const isAudioInputDevice = (device: MediaDeviceInfo): boolean =>
  device.kind === "audioinput";

export const isAudioOutputDevice = (device: MediaDeviceInfo): boolean =>
  device.kind === "audiooutput";

export const hasNonEmptyLabel = (device: MediaDeviceInfo): boolean =>
  device.label !== "";

export const getAudioInputDevices = ({
  devices,
}: DeviceInfoState): MediaDeviceInfo[] => devices.filter(isAudioInputDevice);

export const getAudioInputDevicesWithLabels = (
  deviceInfo: DeviceInfoState,
): MediaDeviceInfo[] =>
  getAudioInputDevices(deviceInfo).filter(hasNonEmptyLabel);

export const getAudioOutputDevices = ({
  devices,
}: DeviceInfoState): MediaDeviceInfo[] => devices.filter(isAudioOutputDevice);

export const getAudioOutputDevicesWithLabels = (
  deviceInfo: DeviceInfoState,
): MediaDeviceInfo[] =>
  getAudioOutputDevices(deviceInfo).filter(hasNonEmptyLabel);

export const getVideoInputDevices = ({
  devices,
}: DeviceInfoState): MediaDeviceInfo[] => devices.filter(isVideoInputDevice);

export const getVideoInputDevicesWithLabels = (
  deviceInfo: DeviceInfoState,
): MediaDeviceInfo[] =>
  getVideoInputDevices(deviceInfo).filter(hasNonEmptyLabel);

export const selectAudioInputDevicesWithLabels = createSelector(
  selectDeviceInfoState,
  getAudioInputDevicesWithLabels,
);

export const selectAudioInputDevices = createSelector(
  selectDeviceInfoState,
  getAudioInputDevices,
);

export const selectAudioOutputDevicesWithLabels = createSelector(
  selectDeviceInfoState,
  getAudioOutputDevicesWithLabels,
);

export const selectAudioOutputDevices = createSelector(
  selectDeviceInfoState,
  getAudioOutputDevices,
);

export const selectVideoInputDevicesWithLabels = createSelector(
  selectDeviceInfoState,
  getVideoInputDevicesWithLabels,
);

export const selectVideoInputDevices = createSelector(
  selectDeviceInfoState,
  getVideoInputDevices,
);

export const selectIsMobile = createSelector(
  selectDeviceInfoState,
  (state) => state.isMobile,
);

export const selectPermissionsState = createSelector(
  selectDeviceInfoState,
  ({ permissions }) => permissions,
);

export const selectHasAudioPermissions = createSelector(
  selectPermissionsState,
  ({ audio }) => audio === PermissionState.GRANTED,
);

export const selectHasVideoPermissions = createSelector(
  selectPermissionsState,
  ({ video }) => video === PermissionState.GRANTED,
);

export const selectActiveDevices = createSelector(
  selectDeviceInfoState,
  ({ activeDevices }) => activeDevices,
);

export const selectActiveAudioInput = createSelector(
  selectActiveDevices,
  ({ audioInput }) => audioInput,
);

export const selectActiveAudioOutput = createSelector(
  selectActiveDevices,
  ({ audioOutput }) => audioOutput,
);

export const selectActiveVideoInput = createSelector(
  selectActiveDevices,
  ({ videoInput }) => videoInput,
);

/**
 * @deprecated use individual selectors from `modules/deviceInfo` instead
 */
export const getDeviceInfo = createSelector(
  selectDeviceInfoState,
  selectAudioOutputDevicesWithLabels,
  selectVideoInputDevicesWithLabels,
  selectAudioInputDevicesWithLabels,
  (deviceInfo, audioOutputDevices, videoInputDevices, audioInputDevices) => {
    const hasSpeakers = audioOutputDevices.length > 0;
    const isSetSinkIdSupported = supportsSetSinkId();

    if (
      navigator &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.addEventListener === "undefined"
    ) {
      navigator.mediaDevices.addEventListener = () => {
        logger.warn("[getDeviceInfo] Calling mocked addEventListener");
      };
    }

    if (
      navigator &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.removeEventListener === "undefined"
    ) {
      navigator.mediaDevices.removeEventListener = () => {
        logger.warn("[getDeviceInfo] Calling mocked removeEventListener");
      };
    }

    return {
      ...deviceInfo,
      hasSpeakers,
      hasWebcam: videoInputDevices.length > 0,
      hasMicrophone: audioInputDevices.length > 0,
      // TODO: extract to const
      hasDevicesAPI: !!navigator?.mediaDevices?.enumerateDevices,
      isSetSinkIdSupported,
      canChangeSpeakers: hasSpeakers && isSetSinkIdSupported,
      audioOutputDevices,
      videoInputDevices,
      audioInputDevices,
    };
  },
);

export const DEFAULT_MEDIA_STREAM_CONSTRAINTS: Readonly<MediaStreamConstraints> =
  {
    audio: true,
    video: true,
  };

export const selectIsAnyAudioInputAvailable = createSelector(
  selectAudioInputDevices,
  (audioInputDevices) => audioInputDevices.length !== 0,
);

export const selectIsAnyVideoInputAvailable = createSelector(
  selectVideoInputDevices,
  (videoInputDevices) => videoInputDevices.length !== 0,
);

export const selectAvailableMediaStreamConstraints = createSelector(
  selectIsAnyAudioInputAvailable,
  selectIsAnyVideoInputAvailable,
  (
    isAnyAudioInputAvailable,
    isAnyVideoInputAvailable,
  ): MediaStreamConstraints => {
    const isAnyAudioInputOrVideoInputAvailable =
      isAnyAudioInputAvailable || isAnyVideoInputAvailable;

    if (!isAnyAudioInputOrVideoInputAvailable)
      return DEFAULT_MEDIA_STREAM_CONSTRAINTS;

    return {
      audio: isAnyAudioInputAvailable,
      video: isAnyVideoInputAvailable,
    };
  },
);

export const selectOpenTokNetworkTestOptions = createSelector(
  selectActiveAudioInput,
  selectActiveVideoInput,
  (activeAudioInput, activeVideoInput): NetworkTestOptions => {
    const hasVideoInput = Boolean(activeVideoInput);

    return {
      audioSource: activeAudioInput?.deviceId,
      videoSource: activeVideoInput?.deviceId,
      audioOnly: !hasVideoInput,
    };
  },
);

const selectPreEnabledDevicesState = createSelector(
  selectDeviceInfoState,
  ({ preEnable }) => preEnable,
);

export const selectIsAudioInputPreEnabled = createSelector(
  selectPreEnabledDevicesState,
  ({ audioInput }) => audioInput,
);

export const selectIsVideoInputPreEnabled = createSelector(
  selectPreEnabledDevicesState,
  ({ videoInput }) => videoInput,
);
